'use client';
import React, { useState, useMemo } from 'react';
import FilterSidebar from './FilterSidebar';
import VenueGrid from './VenueGrid';
import ListingHeader from './ListingHeader';
import { X } from 'lucide-react';

export type VenueFilters = {
  eventTypes: string[];
  priceMin: number;
  priceMax: number;
  capacityMin: number;
  amenities: string[];
  rating: number;
  availability: string;
};

const defaultFilters: VenueFilters = {
  eventTypes: [],
  priceMin: 0,
  priceMax: 300000,
  capacityMin: 0,
  amenities: [],
  rating: 0,
  availability: 'all',
};

export default function VenueListingContent() {
  const [filters, setFilters] = useState<VenueFilters>(defaultFilters);
  const [sortBy, setSortBy] = useState('popularity');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const resetFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.eventTypes.length) count += filters.eventTypes.length;
    if (filters.priceMax < 300000) count++;
    if (filters.capacityMin > 0) count++;
    if (filters.amenities.length) count += filters.amenities.length;
    if (filters.rating > 0) count++;
    if (filters.availability !== 'all') count++;
    return count;
  }, [filters]);

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-10 py-8">
      <ListingHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortBy={sortBy}
        setSortBy={setSortBy}
        onOpenMobileFilters={() => setSidebarOpen(true)}
        activeFilterCount={activeFilterCount}
      />

      <div className="flex gap-6 mt-6">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-72 shrink-0">
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            onReset={resetFilters}
            activeCount={activeFilterCount}
          />
        </aside>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-80 bg-white overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900 text-lg">Filters</h3>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <FilterSidebar
                filters={filters}
                setFilters={setFilters}
                onReset={resetFilters}
                activeCount={activeFilterCount}
              />
            </div>
          </div>
        )}

        {/* Venue Grid */}
        <div className="flex-1 min-w-0">
          <VenueGrid
            filters={filters}
            sortBy={sortBy}
            searchQuery={searchQuery}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}